import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { api, errMsg, cleanPhone } from "@/lib/auth";
import { PageHeader } from "@/pages/Classes";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import EmailOptInCheckbox, { RequiredMark } from "@/components/EmailOptInCheckbox";
import PhotoConsentCheckbox from "@/components/PhotoConsentCheckbox";
import { PromoCodeInput } from "@/components/PromoCodeInput";

const REQUIRED = ["full_name", "email", "phone", "age"];
const LABEL = { full_name: "Full name", email: "Email", phone: "Phone", age: "Age", dance_experience: "Dance experience" };
const TYPE = { email: "email", phone: "tel", age: "number" };

export default function Workshops() {
  const [workshops, setWorkshops] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", age: "", dance_experience: "", notes: "", email_opt_in: false, photo_consent: false });
  const [promo, setPromo] = useState(null);

  useEffect(() => { api.get("/workshops").then(r => setWorkshops(r.data)); }, []);
  const [sp] = useSearchParams();
  const [resume, setResume] = useState(null);
  useEffect(() => {
    const rid = sp.get("resume"), t = sp.get("t");
    if (!rid || !t) return;
    api.get(`/checkout/resume/workshop/${rid}`, { params: { t } }).then(r => {
      if (r.data.already_paid) { toast.success("Good news — this booking is already paid."); return; }
      setForm(f => ({ ...f, ...r.data.form }));
      setResume(r.data);
    }).catch(() => toast.error("That link has expired — please pick your workshop below."));
  }, [sp]);
  useEffect(() => {
    if (!resume || !workshops.length) return;
    const w = workshops.find(x => x.id === (resume.program_id || sp.get("w")));
    if (w) { setSelected(w); toast("Resuming your booking…"); setTimeout(() => submitRef.current?.(), 400); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, workshops]);
  const submitRef = React.useRef(null);
  const today = new Date().toISOString().slice(0,10);
  const upcoming = workshops.filter(w => !w.date || w.date >= today);

  const submit = async () => {
    try {
      const { data } = await api.post("/payments/workshop-checkout", { workshop_id: selected.id, ...form, promo_code: promo?.code || null, age: form.age ? parseInt(form.age): null, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  submitRef.current = submit;

  const missing = REQUIRED.some(k => !String(form[k] || "").trim());

  return (
    <>
      <PageHeader eyebrow="Special events" title="Workshops" sub="Guest choreographers, intensives and family sessions." />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-6">
        {upcoming.map(w => (
          <div key={w.id} className="card-surface overflow-hidden card-hover" data-testid={`workshop-${w.id}`}>
            {w.image && <img src={w.image} className="w-full aspect-video object-cover" alt="" />}
            <div className="p-6">
              <div className="eyebrow mb-2">{w.date} · {w.location_name}</div>
              <h3 className="font-display text-3xl uppercase">{w.name}</h3>
              <p className="text-zinc-400 text-sm mt-3">{w.description}</p>
              <div className="mt-4 text-sm text-zinc-500">{w.hosting === "external" && w.host_name ? `Hosted by ${w.host_name} · ` : ""}{w.instructor} · {fmtRange(w.start_time, w.end_time)}</div>
              <div className="mt-4 flex justify-between items-center border-t border-white/10 pt-4">
                <div className="font-display text-2xl">{w.price > 0 ? `$${w.price}` : (w.hosting === "external" ? <span className="text-xs text-zinc-500 uppercase tracking-widest">See host site</span> : "Free")}</div>
                {w.hosting === "external" ? (
                  <a href={w.external_url || "#"} target="_blank" rel="noopener noreferrer" className="btn-red !py-2 !px-3 !text-xs inline-flex items-center gap-1" data-testid={`register-workshop-${w.id}`}>{w.external_label || "Register on host site"} <ExternalLink size={12}/></a>
                ) : (
                <button onClick={() => setSelected(w)} className="btn-red !py-2 !px-3 !text-xs" data-testid={`register-workshop-${w.id}`}>Register</button>
                )}
              </div>
            </div>
          </div>
        ))}
        {upcoming.length === 0 && <div className="col-span-full text-zinc-500 text-center py-20" data-testid="workshops-empty">No upcoming workshops.</div>}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="card-surface p-8 max-w-md w-full my-8" onClick={e=>e.stopPropagation()}>
            <h3 className="font-display text-3xl uppercase mb-1">{selected.name}</h3>
            <p className="text-zinc-400 text-sm mb-6">{selected.date} · {promo ? <><span className="line-through text-zinc-600">${selected.price}</span> <span className="text-emerald-400">${Number(promo.final).toFixed(2)}</span></> : `$${selected.price}`}</p>
            {["full_name","email","phone","age","dance_experience"].map(k => {
              const req = REQUIRED.includes(k);
              return (
                <div key={k} className="mb-3">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    {LABEL[k]}{req && <RequiredMark/>}
                  </label>
                  <input
                    type={TYPE[k] || "text"}
                    required={req}
                    aria-required={req}
                    placeholder={LABEL[k]}
                    value={form[k]}
                    onChange={e => setForm({...form,[k]: k === "phone" ? cleanPhone(e.target.value) : e.target.value})}
                    className="w-full bg-black border border-white/10 px-4 py-3 focus:border-red-600 outline-none"
                    data-testid={`workshop-${k}`}
                  />
                </div>
              );
            })}
            <EmailOptInCheckbox checked={form.email_opt_in} onChange={v => setForm({...form, email_opt_in: v})} testId="workshop-email-opt-in"/>
            <PhotoConsentCheckbox checked={form.photo_consent} onChange={v => setForm({...form, photo_consent: v})} testId="workshop-photo-consent"/>
            <PromoCodeInput type="workshop" amount={selected.price} onApplied={setPromo} testId="workshop-promo"/>
            <button onClick={submit} disabled={missing} className="btn-red w-full disabled:opacity-50" data-testid="submit-workshop-reg">Submit</button>
          </div>
        </div>
      )}
    </>
  );
}
